import React, { useState, useCallback, useEffect } from 'react'
import styled from '@emotion/styled'
import Modal from '@components/common/modal/Modal'
import { ScheduleJson, ConceptSummary } from './useMenuGeneration'
import { DateMenus } from '../../../../types/groupOrder/dateMenus'
import {
  MenuConcept,
  MenuItem,
} from '@components/common/modal/PickMenuModal/types'
import services from '@services'
import presenters from '@presenters'
import { HandleError } from '@coordinators/composed'
import { GetGroupOrderConcept } from '@coordinators/chef/concept'
import AcceptScheduleModal from './AcceptScheduleModal'
import { formatCutoffHours, formatCutoff } from './menuGenerationUtils'

const getGroupOrderConcept = GetGroupOrderConcept({
  RestService: services.RestService,
  pResponseGroupOrderConcept: presenters.Api.pResponseGroupOrderConcept,
  HandleError,
})

const VISIBLE_DIETARY_TAGS = new Set([
  'halal',
  'kosher',
  'vegetarian',
  'vegan',
  'paleo',
])

interface AcceptResult {
  created: { date: string; dateMenuId: string }[]
  errors: { date: string; menuId?: string; message: string }[]
  previews?: DateMenus[]
  dateMenus?: DateMenus[]
}

interface ProgressInfo {
  current: number
  total: number
  dateStr: string
}

interface MenuGenerationScheduleProps {
  schedule: ScheduleJson
  conceptMap: Record<string, ConceptSummary> | null
  onAcceptSchedule: () => void
  isAccepting: boolean
  acceptResult: AcceptResult | null
  acceptProgress: ProgressInfo | null
  acceptError: string | null
  onCreateMenus: (activeMap: Record<string, boolean>) => Promise<any>
  isCreating: boolean
  createResult: AcceptResult | null
  createProgress: ProgressInfo | null
  onCloseAll?: () => void
}

const MenuGenerationSchedule: React.FC<MenuGenerationScheduleProps> = ({
  schedule,
  conceptMap,
  onAcceptSchedule,
  isAccepting,
  acceptResult,
  acceptProgress,
  acceptError,
  onCreateMenus,
  isCreating,
  createResult,
  createProgress,
  onCloseAll,
}) => {
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null)
  const [conceptData, setConceptData] = useState<MenuConcept | null>(null)
  const [isLoadingConcept, setIsLoadingConcept] = useState(false)
  const [conceptError, setConceptError] = useState<string | null>(null)
  const [showAcceptModal, setShowAcceptModal] = useState(false)
  const [resultsModalSource, setResultsModalSource] = useState<
    'accept' | 'create' | null
  >(null)

  // After accept completes: show error modal if errors, otherwise open AcceptScheduleModal
  useEffect(() => {
    if (isAccepting || !acceptResult) {
      return
    }
    if (acceptResult.errors && acceptResult.errors.length > 0) {
      setResultsModalSource('accept')
    } else if (acceptResult.previews && acceptResult.previews.length > 0) {
      setShowAcceptModal(true)
    }
  }, [acceptResult, isAccepting])

  // After create completes: always show results modal
  useEffect(() => {
    if (isCreating || !createResult) {
      return
    }
    setResultsModalSource('create')
  }, [createResult, isCreating])

  const handleMenuClick = useCallback(
    async (menuId: string) => {
      if (selectedMenuId === menuId) {
        return
      }
      setSelectedMenuId(menuId)
      setConceptData(null)
      setConceptError(null)
      setIsLoadingConcept(true)
      try {
        const concept = await getGroupOrderConcept(menuId)
        setConceptData(concept)
      } catch (err) {
        setConceptError('Failed to load menu details.')
      } finally {
        setIsLoadingConcept(false)
      }
    },
    [selectedMenuId],
  )

  const handleCloseDetail = useCallback(() => {
    setSelectedMenuId(null)
    setConceptData(null)
    setConceptError(null)
  }, [])

  return (
    <ScheduleContainer>
      <ScrollableContent>
        <ScheduleHeader>
          <h3>{schedule.client}</h3>
          <span>{schedule.month}</span>
        </ScheduleHeader>
        <Table>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Day</Th>
              <Th>Chef</Th>
              <Th>Menu</Th>
              <Th>Cuisines</Th>
              <Th>Dietary</Th>
              <Th>Cutoff</Th>
            </tr>
          </thead>
          <tbody>
            {schedule.schedule.map((day) =>
              day.menus.map((menu, menuIdx) => (
                <tr key={`${day.date}-${menuIdx}`}>
                  {menuIdx === 0 ? (
                    <>
                      <Td rowSpan={day.menus.length}>{day.date}</Td>
                      <Td rowSpan={day.menus.length}>{day.day}</Td>
                    </>
                  ) : null}
                  <Td>{menu.chef_name}</Td>
                  <Td>
                    <MenuLink onClick={() => handleMenuClick(menu.menu_id)}>
                      {menu.menu_name}
                    </MenuLink>
                  </Td>
                  <Td>{menu.cuisines.join(', ')}</Td>
                  <Td>
                    {conceptMap?.[menu.menu_id]?.dietaryPreferenceTags
                      ?.filter((t: string) =>
                        VISIBLE_DIETARY_TAGS.has(t.toLowerCase()),
                      )
                      .join(', ') || '-'}
                  </Td>
                  <Td>
                    {conceptMap?.[menu.menu_id]
                      ? formatCutoff(conceptMap[menu.menu_id])
                      : '-'}
                  </Td>
                </tr>
              )),
            )}
          </tbody>
        </Table>

        {selectedMenuId && (
          <Modal
            title={
              isLoadingConcept
                ? 'Loading...'
                : conceptData
                  ? conceptData.name
                  : 'Menu Details'
            }
            hideModal={handleCloseDetail}
            width="700px"
          >
            {isLoadingConcept && (
              <DetailLoading>Loading menu details...</DetailLoading>
            )}

            {conceptError && <ErrorMessage>{conceptError}</ErrorMessage>}

            {conceptData && (
              <DetailBody>
                <DetailRow>
                  <DetailLabel>Chef</DetailLabel>
                  <DetailValue>{conceptData.chef.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Cutoff</DetailLabel>
                  <DetailValue>
                    {conceptData.cutoffDays !== null
                      ? `${conceptData.cutoffDays} day${
                          conceptData.cutoffDays !== 1 ? 's' : ''
                        } before`
                      : 'N/A'}
                    {conceptData.cutoffHours !== null &&
                      ` at ${formatCutoffHours(conceptData.cutoffHours)}`}
                    {conceptData.cutoffDays !== null &&
                      ` (weekends ${
                        conceptData.cutoffIncludesWeekends
                          ? 'included'
                          : 'excluded'
                      })`}
                  </DetailValue>
                </DetailRow>
                {conceptData.cuisines.length > 0 && (
                  <DetailRow>
                    <DetailLabel>Cuisines</DetailLabel>
                    <DetailValue>{conceptData.cuisines.join(', ')}</DetailValue>
                  </DetailRow>
                )}

                <MenuItemsSection>
                  <MenuItemsTitle>
                    Menu Items ({conceptData.menuItems.length})
                  </MenuItemsTitle>
                  <MenuItemsTable>
                    <thead>
                      <tr>
                        <MenuItemTh>Name</MenuItemTh>
                        <MenuItemTh>Type</MenuItemTh>
                        <MenuItemTh>Price</MenuItemTh>
                        <MenuItemTh>Dietary</MenuItemTh>
                        <MenuItemTh>Description</MenuItemTh>
                      </tr>
                    </thead>
                    <tbody>
                      {conceptData.menuItems.map((item: MenuItem) => (
                        <tr key={item.id}>
                          <MenuItemTd>{item.name}</MenuItemTd>
                          <MenuItemTd>{item.itemType}</MenuItemTd>
                          <MenuItemTd>${item.price.toFixed(2)}</MenuItemTd>
                          <MenuItemTd>
                            {item.dietaryPrefs.length > 0
                              ? item.dietaryPrefs.join(', ')
                              : '-'}
                          </MenuItemTd>
                          <MenuItemTd>
                            <DescriptionText>
                              {item.description || '-'}
                            </DescriptionText>
                          </MenuItemTd>
                        </tr>
                      ))}
                    </tbody>
                  </MenuItemsTable>
                </MenuItemsSection>
              </DetailBody>
            )}
          </Modal>
        )}
      </ScrollableContent>
      <AcceptSection>
        <AcceptButton
          onClick={onAcceptSchedule}
          disabled={isAccepting || isCreating}
        >
          {isAccepting && acceptProgress
            ? `Validating ${acceptProgress.current} of ${acceptProgress.total}...`
            : isAccepting
              ? 'Validating Schedule...'
              : 'Validate Schedule'}
        </AcceptButton>
        {createResult &&
          createResult.created &&
          createResult.created.length > 0 && (
            <SuccessMessage>
              Created {createResult.created.length} date menu
              {createResult.created.length !== 1 ? 's' : ''}.
            </SuccessMessage>
          )}
        {((acceptResult?.errors?.length ?? 0) > 0 ||
          (createResult?.errors?.length ?? 0) > 0) && (
          <ErrorLink
            onClick={() =>
              setResultsModalSource(
                createResult?.errors?.length ? 'create' : 'accept',
              )
            }
          >
            {(acceptResult?.errors?.length ?? 0) +
              (createResult?.errors?.length ?? 0)}{' '}
            error
            {(acceptResult?.errors?.length ?? 0) +
              (createResult?.errors?.length ?? 0) !==
            1
              ? 's'
              : ''}{' '}
            — click to view
          </ErrorLink>
        )}
        {acceptError && <ErrorMessage>{acceptError}</ErrorMessage>}
      </AcceptSection>

      {showAcceptModal && acceptResult?.previews && (
        <AcceptScheduleModal
          previews={acceptResult.previews}
          //previews={testPreviews}
          onCreateMenus={async (activeMap) => {
            await onCreateMenus(activeMap)
            // Results modal will be shown by the useEffect above
          }}
          onClose={() => setShowAcceptModal(false)}
          isCreating={isCreating}
          createProgress={createProgress}
        />
      )}
      {resultsModalSource &&
        (() => {
          const errors =
            resultsModalSource === 'accept'
              ? acceptResult?.errors || []
              : createResult?.errors || []
          const succeededDates =
            resultsModalSource === 'accept'
              ? (acceptResult?.previews || []).map((p) => p.dateStr)
              : (createResult?.created || []).map((c) => c.date)
          const hasSuccesses = succeededDates.length > 0

          return (
            <Modal
              title={
                resultsModalSource === 'accept'
                  ? 'Validate Schedule Results'
                  : 'Create Menus Results'
              }
              hideModal={() => {
                if (resultsModalSource === 'create' && hasSuccesses) {
                  setResultsModalSource(null)
                  setShowAcceptModal(false)
                  onCloseAll?.()
                } else {
                  setResultsModalSource(null)
                }
              }}
              width="600px"
            >
              <ErrorModalBody>
                {hasSuccesses && (
                  <ResultSection>
                    <ResultSectionTitle $variant="success">
                      Succeeded ({succeededDates.length})
                    </ResultSectionTitle>
                    <SucceededList>
                      {succeededDates.map((date) => (
                        <SucceededItem key={date}>{date}</SucceededItem>
                      ))}
                    </SucceededList>
                  </ResultSection>
                )}
                {errors.length > 0 && (
                  <ResultSection>
                    <ResultSectionTitle $variant="error">
                      Failed ({errors.length})
                    </ResultSectionTitle>
                    <ErrorTable>
                      <thead>
                        <tr>
                          <ErrorTh>Date</ErrorTh>
                          <ErrorTh>Error</ErrorTh>
                        </tr>
                      </thead>
                      <tbody>
                        {errors.map((err, idx) => (
                          <tr key={idx}>
                            <ErrorTd>{err.date}</ErrorTd>
                            <ErrorTd>{err.message}</ErrorTd>
                          </tr>
                        ))}
                      </tbody>
                    </ErrorTable>
                  </ResultSection>
                )}
              </ErrorModalBody>
              <ModalFooter>
                {resultsModalSource === 'accept' ? (
                  <>
                    <FooterButton onClick={() => setResultsModalSource(null)}>
                      Cancel
                    </FooterButton>
                    {hasSuccesses && (
                      <FooterButton
                        $primary
                        onClick={() => {
                          setResultsModalSource(null)
                          setShowAcceptModal(true)
                        }}
                      >
                        Continue to final review of {succeededDates.length}{' '}
                        succeeded
                      </FooterButton>
                    )}
                  </>
                ) : hasSuccesses ? (
                  <FooterButton
                    $primary
                    onClick={() => {
                      setResultsModalSource(null)
                      setShowAcceptModal(false)
                      onCloseAll?.()
                    }}
                  >
                    Done
                  </FooterButton>
                ) : (
                  <FooterButton onClick={() => setResultsModalSource(null)}>
                    Close
                  </FooterButton>
                )}
              </ModalFooter>
            </Modal>
          )
        })()}
    </ScheduleContainer>
  )
}

const ScheduleContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 16px 0;
`

const ScheduleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 16px;
  }

  span {
    color: #666;
    font-size: 14px;
  }
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  border-bottom: 2px solid #e0e0e0;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
`

const Td = styled.td`
  padding: 6px 10px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`

const MenuLink = styled.span`
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #1d4ed8;
  }
`

const DetailLoading = styled.div`
  padding: 20px 14px;
  color: #666;
  font-size: 13px;
`

const DetailBody = styled.div`
  padding: 12px 14px;
`

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
`

const DetailLabel = styled.span`
  font-weight: 600;
  color: #555;
  min-width: 60px;
`

const DetailValue = styled.span`
  color: #333;
`

const MenuItemsSection = styled.div`
  margin-top: 12px;
`

const MenuItemsTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #333;
`

const MenuItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`

const MenuItemTh = styled.th`
  text-align: left;
  padding: 5px 8px;
  border-bottom: 1px solid #ddd;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
`

const MenuItemTd = styled.td`
  padding: 4px 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`

const DescriptionText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 200px;
`

const AcceptSection = styled.div`
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
`

const AcceptButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SuccessMessage = styled.div`
  margin-top: 8px;
  color: #16a34a;
  font-size: 13px;
`

const ErrorMessage = styled.div`
  margin-top: 4px;
  color: #dc2626;
  font-size: 13px;
`

const ErrorLink = styled.span`
  display: inline-block;
  margin-top: 8px;
  margin-left: 12px;
  color: #dc2626;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #b91c1c;
  }
`

const ErrorModalBody = styled.div`
  padding: 12px 14px;
  max-height: 400px;
  overflow-y: auto;
`

const ErrorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const ErrorTh = styled.th`
  text-align: left;
  padding: 6px 10px;
  border-bottom: 2px solid #e0e0e0;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
`

const ErrorTd = styled.td`
  padding: 6px 10px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
  color: #333;
`

const ResultSection = styled.div`
  margin-bottom: 16px;
`

const ResultSectionTitle = styled.div<{ $variant: 'success' | 'error' }>`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: ${({ $variant }) => ($variant === 'success' ? '#16a34a' : '#dc2626')};
`

const SucceededList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const SucceededItem = styled.span`
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 12px;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #e0e0e0;
`

const FooterButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? '#2563eb' : '#d1d5db')};
  background: ${({ $primary }) => ($primary ? '#2563eb' : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : '#333')};

  &:hover {
    background: ${({ $primary }) => ($primary ? '#1d4ed8' : '#f3f4f6')};
  }
`

export default MenuGenerationSchedule
